/**
 * Field-mapping engine.
 *
 * `resolveField(obj, path, transform?)` walks a JSONPath-like dotted/bracketed
 * path into any nested object/array and applies an optional transform. This is
 * what lets the GenericHttpAdapter map every supplier's differently-shaped JSON
 * onto our NormalisedProduct fields with zero per-supplier code.
 *
 * Supported path syntax:
 *   data.items[0].price
 *   data.items.0.price
 *   result["weird key"].value
 *   items[-1].id            (negative index counts from the end)
 *   data.*.price            (wildcard — returns the first non-nullish match)
 */

import type { FieldTransform, OurField, SupplierFieldMapping } from './types.js';

// ─────────────────────────────────────────────────────────────
// Path parsing
// ─────────────────────────────────────────────────────────────

export type PathSegment = { kind: 'key'; value: string } | { kind: 'index'; value: number } | { kind: 'wildcard' };

const SEGMENT_RE =
  // .key   |   ["quoted key"]   |   ['quoted key']   |   [123]   |   [*]   |   leading key
  /\.([^.[\]]+)|\["([^"]*)"\]|\['([^']*)'\]|\[(-?\d+)\]|\[\*\]|^([^.[\]]+)/g;

export function parsePath(path: string): PathSegment[] {
  if (typeof path !== 'string' || path.trim() === '') return [];
  const segments: PathSegment[] = [];
  const normalised = path.trim();
  let match: RegExpExecArray | null;
  SEGMENT_RE.lastIndex = 0;
  let lastIndex = 0;

  while ((match = SEGMENT_RE.exec(normalised)) !== null) {
    // Detect gaps (invalid characters between tokens) — bail rather than silently skip.
    if (match.index !== lastIndex) break;
    lastIndex = SEGMENT_RE.lastIndex;

    const [, dotKey, dqKey, sqKey, bracketIdx, leadKey] = match;
    if (dotKey !== undefined) {
      if (dotKey === '*') segments.push({ kind: 'wildcard' });
      else segments.push({ kind: 'key', value: dotKey });
    } else if (dqKey !== undefined) {
      segments.push({ kind: 'key', value: dqKey });
    } else if (sqKey !== undefined) {
      segments.push({ kind: 'key', value: sqKey });
    } else if (bracketIdx !== undefined) {
      segments.push({ kind: 'index', value: Number.parseInt(bracketIdx, 10) });
    } else if (leadKey !== undefined) {
      if (leadKey === '*') segments.push({ kind: 'wildcard' });
      else segments.push({ kind: 'key', value: leadKey });
    } else {
      // Bare `[*]`
      segments.push({ kind: 'wildcard' });
    }
  }
  return segments;
}

// ─────────────────────────────────────────────────────────────
// Path resolution
// ─────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stepInto(current: unknown, segment: PathSegment): unknown {
  if (current == null) return undefined;

  if (segment.kind === 'wildcard') {
    const values = Array.isArray(current)
      ? current
      : isRecord(current)
        ? Object.values(current)
        : [];
    return values.find((v) => v != null);
  }

  if (segment.kind === 'index') {
    if (!Array.isArray(current)) return undefined;
    const idx = segment.value < 0 ? current.length + segment.value : segment.value;
    return idx >= 0 && idx < current.length ? current[idx] : undefined;
  }

  // key
  if (Array.isArray(current)) {
    // Allow numeric string keys against arrays: items.0.price
    const asNum = Number(segment.value);
    if (Number.isInteger(asNum)) {
      const idx = asNum < 0 ? current.length + asNum : asNum;
      return idx >= 0 && idx < current.length ? current[idx] : undefined;
    }
    return undefined;
  }
  if (isRecord(current)) {
    return current[segment.value];
  }
  return undefined;
}

/** Walk `path` into `obj`. Returns `undefined` when any segment is missing. */
export function getByPath(obj: unknown, path: string): unknown {
  const segments = parsePath(path);
  if (segments.length === 0) return undefined;
  let current: unknown = obj;
  for (const segment of segments) {
    current = stepInto(current, segment);
    if (current === undefined) return undefined;
  }
  return current;
}

// ─────────────────────────────────────────────────────────────
// Transforms
// ─────────────────────────────────────────────────────────────

/**
 * Parse a human duration string into a number of days.
 *   "1 month"  -> 30      "3 months" -> 90
 *   "1 year"   -> 365     "2 weeks"  -> 14
 *   "7 days"   -> 7       "24h"      -> 1
 *   "lifetime" -> 36500   "30"       -> 30
 */
export function parseDuration(input: unknown): number {
  if (typeof input === 'number' && Number.isFinite(input)) return Math.round(input);
  if (typeof input !== 'string') return 0;

  const text = input.trim().toLowerCase();
  if (text === '') return 0;
  if (/(lifetime|forever|permanent|life\s*time)/.test(text)) return 36500;

  // Pure number → days.
  if (/^-?\d+(\.\d+)?$/.test(text)) return Math.max(0, Math.round(Number(text)));

  const match = text.match(/(-?\d+(?:\.\d+)?)\s*(year|yr|y|month|mo|m|week|wk|w|day|d|hour|hr|h)/);
  if (!match) return 0;
  const value = Number(match[1]);
  const unit = match[2] ?? '';
  const perUnitDays: Record<string, number> = {
    year: 365, yr: 365, y: 365,
    month: 30, mo: 30, m: 30,
    week: 7, wk: 7, w: 7,
    day: 1, d: 1,
    hour: 1 / 24, hr: 1 / 24, h: 1 / 24,
  };
  const days = value * (perUnitDays[unit] ?? 0);
  return Math.max(0, Math.round(days));
}

const TRUTHY = new Set(['true', '1', 'yes', 'y', 'in_stock', 'instock', 'available', 'on']);
const FALSY = new Set(['false', '0', 'no', 'n', 'out_of_stock', 'outofstock', 'unavailable', 'off', '']);

export function toBoolean(input: unknown): boolean {
  if (typeof input === 'boolean') return input;
  if (typeof input === 'number') return input > 0;
  if (typeof input === 'string') {
    const t = input.trim().toLowerCase();
    if (TRUTHY.has(t)) return true;
    if (FALSY.has(t)) return false;
    return t.length > 0;
  }
  return Boolean(input);
}

export function toNumber(input: unknown): number {
  if (typeof input === 'number') return Number.isFinite(input) ? input : 0;
  if (typeof input === 'string') {
    // Keep digits, sign, decimal point; drop currency symbols / thousands separators.
    const cleaned = input.replace(/[^0-9.\-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof input === 'boolean') return input ? 1 : 0;
  return 0;
}

export function applyTransform(value: unknown, transform?: FieldTransform): unknown {
  if (transform === undefined) return value;
  switch (transform) {
    case 'toString':
      return value == null ? '' : String(value);
    case 'toNumber':
      return toNumber(value);
    case 'toBoolean':
      return toBoolean(value);
    case 'toLowerCase':
      return value == null ? '' : String(value).toLowerCase();
    case 'toUpperCase':
      return value == null ? '' : String(value).toUpperCase();
    case 'parseDuration':
      return parseDuration(value);
    default: {
      // Exhaustiveness guard.
      const _never: never = transform;
      return _never;
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────

export function resolveField(obj: unknown, path: string, transform?: FieldTransform): unknown {
  const raw = getByPath(obj, path);
  return applyTransform(raw, transform);
}

import type { RawSupplierProduct, AccessType } from './types.js';
import { ACCESS_TYPES } from './types.js';

function coerceAccessType(value: unknown): AccessType {
  const t = typeof value === 'string' ? value.trim().toLowerCase().replace(/[\s-]+/g, '_') : '';
  return (ACCESS_TYPES as readonly string[]).includes(t) ? (t as AccessType) : 'unknown';
}

/**
 * Apply a supplier's field mappings to one raw item from its product-list
 * response, producing a partially-normalised {@link RawSupplierProduct}.
 * Missing/invalid values fall back to safe defaults so a single bad row never
 * breaks a whole sync.
 */
export function mapSupplierProduct(
  item: unknown,
  mappings: readonly SupplierFieldMapping[],
): RawSupplierProduct {
  const out: Record<string, unknown> = {};
  for (const mapping of mappings) {
    const resolved = resolveField(item, mapping.supplierPath, mapping.transform);
    out[mapping.ourField] = mapping.multiplier == null ? resolved : toNumber(resolved) * mapping.multiplier;
  }

  const externalId = out['externalId'] != null ? String(out['externalId']) : '';
  const name = out['name'] != null ? cleanSupplierText(String(out['name'])) : '';
  const price = toNumber(out['price']);
  const currency = out['currency'] != null ? String(out['currency']).toUpperCase() : 'USDT';
  const inStock = out['inStock'] === undefined ? true : toBoolean(out['inStock']);
  const stockSource = out['stockCount'] ?? out['inStock'];
  const numericStock = typeof stockSource === 'number' || (typeof stockSource === 'string' && stockSource.trim() !== '' && Number.isFinite(Number(stockSource)))
    ? Math.max(0, Math.floor(Number(stockSource)))
    : undefined;

  const result: RawSupplierProduct = {
    externalId,
    name,
    price,
    currency,
    inStock,
    stockCount: numericStock ?? (inStock ? 1 : 0),
    raw: item,
  };
  if (out['duration'] !== undefined) result.durationDays = parseDuration(out['duration']);
  if (out['region'] !== undefined) result.region = String(out['region']).toLowerCase();
  if (out['accessType'] !== undefined) result.accessType = coerceAccessType(out['accessType']);
  if (out['warrantyDays'] !== undefined) result.warrantyDays = toNumber(out['warrantyDays']);
  if (out['description'] !== undefined) result.description = cleanSupplierText(String(out['description']));
  if (out['category'] !== undefined) result.category = String(out['category']);
  return result;
}

/** Remove rich-text placeholders that only render inside supplier clients. */
export function cleanSupplierText(value: string): string {
  return value
    .replace(/\{pe:\d+\}/gi, '')
    .replace(/<\/?tg-emoji\b[^>]*>/gi, '')
    .replace(/<\/?blockquote\b[^>]*>/gi, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/**
 * Build a preview of what each mapping would resolve to against a sample
 * response. Powers the admin panel's visual field-mapper.
 */
export function previewMappings(
  sample: unknown,
  mappings: readonly SupplierFieldMapping[],
): Array<{ ourField: string; supplierPath: string; resolved: unknown; transformed: unknown }> {
  return mappings.map((m) => ({
    ourField: m.ourField,
    supplierPath: m.supplierPath,
    resolved: getByPath(sample, m.supplierPath),
    transformed: resolveField(sample, m.supplierPath, m.transform),
  }));
}

// ─────────────────────────────────────────────────────────────
// Locating the product array in an arbitrary response
// ─────────────────────────────────────────────────────────────

/**
 * Breadth-first search for the most plausible "list of products" array inside a
 * response, returning both the array and the dot-path it lives at
 * (`''` when the response is itself an array). Picks the array with the most
 * object entries.
 */
export function locateProductArray(payload: unknown): { items: unknown[]; path: string } {
  if (Array.isArray(payload)) return { items: payload, path: '' };

  let best: { items: unknown[]; path: string } = { items: [], path: '' };
  const queue: Array<{ node: unknown; path: string }> = [{ node: payload, path: '' }];
  let guard = 0;

  while (queue.length > 0 && guard < 10_000) {
    guard += 1;
    const { node, path } = queue.shift() as { node: unknown; path: string };
    if (Array.isArray(node)) {
      const objectCount = node.filter((x) => x != null && typeof x === 'object').length;
      if (objectCount > best.items.length) best = { items: node, path };
      continue;
    }
    if (node != null && typeof node === 'object') {
      for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        if (value != null && typeof value === 'object') {
          const childPath = /^[A-Za-z_$][\w$]*$/.test(key)
            ? `${path ? `${path}.` : ''}${key}`
            : `${path}["${key}"]`;
          queue.push({ node: value, path: childPath });
        }
      }
    }
  }
  return best;
}

// ─────────────────────────────────────────────────────────────
// Auto-matching supplier fields → our normalised fields
// ─────────────────────────────────────────────────────────────

export type MatchConfidence = 'high' | 'medium' | 'none';

export interface AutoMatchEntry {
  ourField: OurField;
  supplierPath: string | null;
  transform?: FieldTransform;
  /** Raw value at the matched path in the sample item. */
  sampleValue: unknown;
  /** Value after the suggested transform. */
  resolvedValue: unknown;
  confidence: MatchConfidence;
}

export interface AutoMatchResult {
  /** Suggested mappings (only the fields that matched). */
  mappings: SupplierFieldMapping[];
  /** One entry per normalised field, matched or not — drives the results table. */
  entries: AutoMatchEntry[];
  matchedCount: number;
  unmatched: OurField[];
}

interface FieldMatcher {
  ourField: OurField;
  /** Normalised exact key names → high confidence (100). */
  exact: string[];
  /** Normalised exact key names → good but lower priority than `exact` (75). */
  weak?: string[];
  /** Normalised substrings → medium confidence (55). */
  contains: string[];
  transform?: FieldTransform;
}

const FIELD_MATCHERS: FieldMatcher[] = [
  {
    ourField: 'externalId',
    exact: [
      'id', 'productid', 'product', 'serviceid', 'service', 'sku', 'code', 'offerid',
      'externalid', 'itemid', 'variantid', 'uuid', 'ref', 'reference',
    ],
    contains: ['productid', 'serviceid', 'externalid', 'itemid', 'sku'],
    transform: 'toString',
  },
  {
    ourField: 'name',
    exact: ['name', 'title', 'productname', 'producttitle', 'servicename', 'label', 'itemname'],
    contains: ['name', 'title'],
  },
  {
    ourField: 'price',
    exact: ['price', 'cost', 'amount', 'priceusdt', 'priceusd', 'unitprice', 'sellprice', 'sellingprice', 'rate'],
    weak: ['value'],
    contains: ['price', 'cost', 'amount', 'usdt'],
    transform: 'toNumber',
  },
  {
    ourField: 'currency',
    exact: ['currency', 'curr', 'ccy', 'currencycode'],
    contains: ['currency'],
  },
  {
    ourField: 'inStock',
    // Boolean "can I buy this" flags win over bare counts.
    exact: ['instock', 'available', 'isavailable', 'orderable', 'buyable', 'purchasable', 'isactive', 'enabled'],
    weak: ['stock', 'quantity', 'qty', 'stockcount', 'inventory', 'availability', 'active', 'count'],
    contains: ['instock', 'orderable', 'avail'],
    transform: 'toBoolean',
  },
  {
    ourField: 'stockCount',
    exact: ['stockcount', 'stock', 'quantity', 'qty', 'inventory', 'availablecount', 'remaining'],
    contains: ['stockcount', 'quantity', 'inventory', 'remaining'],
    transform: 'toNumber',
  },
  {
    ourField: 'category',
    exact: ['category', 'categoryname', 'group', 'type'],
    contains: ['category'],
  },
  {
    ourField: 'duration',
    exact: [
      'duration', 'validity', 'period', 'term', 'days', 'durationdays', 'expiresin',
      'ttl', 'length', 'subscriptionperiod', 'validfor', 'lifetime',
    ],
    contains: ['duration', 'validity', 'period', 'expires'],
    transform: 'parseDuration',
  },
  {
    ourField: 'region',
    exact: ['region', 'country', 'geo', 'locale', 'area', 'territory', 'countrycode', 'market'],
    contains: ['region', 'country', 'geo'],
  },
  {
    ourField: 'accessType',
    exact: ['accesstype', 'type', 'deliverytype', 'kind', 'producttype', 'accountkind', 'delivery'],
    contains: ['accesstype', 'deliverytype', 'accounttype'],
  },
  {
    ourField: 'warrantyDays',
    exact: ['warranty', 'warrantydays', 'guarantee', 'guaranteedays', 'replacementdays', 'warrantyperiod'],
    contains: ['warranty', 'guarantee'],
    transform: 'toNumber',
  },
  {
    ourField: 'description',
    exact: ['description', 'desc', 'details', 'info', 'note', 'notes', 'summary', 'about', 'text'],
    contains: ['description', 'details'],
  },
];

function normaliseKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Last meaningful segment of a leaf path, e.g. `data.items[0].price` → `price`. */
function lastSegment(path: string): string {
  const cleaned = path.replace(/\[\d+\]/g, '');
  const parts = cleaned.split('.').filter(Boolean);
  return parts[parts.length - 1] ?? path;
}

/**
 * Guess field mappings from a single sample item. Existing mappings are kept
 * untouched — auto-match only fills the gaps unless `overwrite` is set.
 */
export function autoMatchMappings(
  sampleItem: unknown,
  options: { existing?: readonly SupplierFieldMapping[]; overwrite?: boolean } = {},
): AutoMatchResult {
  const existing = options.existing ?? [];
  const existingByField = new Map(existing.map((m) => [m.ourField, m]));
  const leaves = flattenJsonLeaves(sampleItem).filter((l) => l.path !== '$');

  const entries: AutoMatchEntry[] = [];
  const mappings: SupplierFieldMapping[] = [];

  for (const matcher of FIELD_MATCHERS) {
    const kept = existingByField.get(matcher.ourField);
    if (kept && !options.overwrite) {
      const transformed = resolveField(sampleItem, kept.supplierPath, kept.transform);
      entries.push({
        ourField: matcher.ourField,
        supplierPath: kept.supplierPath,
        transform: kept.transform,
        sampleValue: getByPath(sampleItem, kept.supplierPath),
        resolvedValue: transformed,
        confidence: 'high',
      });
      mappings.push(kept);
      continue;
    }

    let best: { path: string; score: number } | null = null;
    for (const leaf of leaves) {
      const seg = normaliseKey(lastSegment(leaf.path));
      if (!seg) continue;
      let score = 0;
      if (matcher.exact.includes(seg)) score = 100;
      else if (matcher.exact.some((e) => seg === `${e}s` || `${seg}s` === e)) score = 90;
      else if (matcher.weak?.includes(seg)) score = 75;
      else if (matcher.contains.some((c) => seg.includes(c))) score = 55;
      else if (matcher.exact.some((e) => seg.includes(e) && e.length >= 4)) score = 45;
      if (score === 0) continue;

      // Prefer shallow paths and non-null sample values.
      const depth = (leaf.path.match(/\./g)?.length ?? 0) + (leaf.path.match(/\[/g)?.length ?? 0);
      score -= depth * 3;
      if (leaf.value == null) score -= 8;

      if (!best || score > best.score) best = { path: leaf.path, score };
    }

    if (best && best.score >= 40) {
      const confidence: MatchConfidence = best.score >= 85 ? 'high' : 'medium';
      const mapping: SupplierFieldMapping = { ourField: matcher.ourField, supplierPath: best.path };
      if (matcher.transform) mapping.transform = matcher.transform;
      mappings.push(mapping);
      entries.push({
        ourField: matcher.ourField,
        supplierPath: best.path,
        transform: matcher.transform,
        sampleValue: getByPath(sampleItem, best.path),
        resolvedValue: resolveField(sampleItem, best.path, matcher.transform),
        confidence,
      });
    } else {
      entries.push({
        ourField: matcher.ourField,
        supplierPath: null,
        sampleValue: undefined,
        resolvedValue: undefined,
        confidence: 'none',
      });
    }
  }

  const unmatched = entries.filter((e) => e.supplierPath === null).map((e) => e.ourField);
  return { mappings, entries, matchedCount: mappings.length, unmatched };
}

/** Merge suggested mappings into existing ones (suggested wins unless the field was already set). */
export function mergeMappings(
  existing: readonly SupplierFieldMapping[],
  incoming: readonly SupplierFieldMapping[],
  options: { overwrite?: boolean } = {},
): SupplierFieldMapping[] {
  const byField = new Map<OurField, SupplierFieldMapping>();
  for (const m of existing) byField.set(m.ourField, m);
  for (const m of incoming) {
    if (options.overwrite || !byField.has(m.ourField)) byField.set(m.ourField, m);
  }
  return [...byField.values()];
}

/** Flatten any JSON value into `{ path, value }` leaves — used by the field-mapper tree UI. */
export function flattenJsonLeaves(
  value: unknown,
  basePath = '',
  acc: Array<{ path: string; value: unknown }> = [],
): Array<{ path: string; value: unknown }> {
  if (Array.isArray(value)) {
    value.forEach((v, i) => flattenJsonLeaves(v, `${basePath}[${i}]`, acc));
  } else if (isRecord(value)) {
    for (const [k, v] of Object.entries(value)) {
      const key = /^[A-Za-z_$][\w$]*$/.test(k) ? `${basePath ? `${basePath}.` : ''}${k}` : `${basePath}["${k}"]`;
      flattenJsonLeaves(v, key, acc);
    }
  } else {
    acc.push({ path: basePath || '$', value });
  }
  return acc;
}
